import { ArrayParam, DecodedValueMap, StringParam, useQueryParams } from 'use-query-params'

import {
  DisbursementBatchesSortDirection,
  GetDisbursementBatchesListProps,
  isDisbursementBatchesDate,
  isDisbursementBatchesSortDirection,
  isDisbursementBatchesSortField
} from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'
import { DisbursementsTableQueryParams } from '@percent/admin-dashboard/common/queryParams/queryParams.types'
import { DisbursementsTable } from './disbursementsTable/DisbursementsTable'
import { DisbursementsTableLegacy } from './disbursementsTable/DisbursementsTableLegacy'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useDidMountEffect, useQueryList } from '@percent/admin-dashboard/common/hooks'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'

/**
 * Sent only as a complete pair. A half-open range is what a truncated shared link leaves behind, and
 * the picker cannot clear one — Lemonade's DateRange disables both Apply and Clear while either end
 * is missing — so a lone bound would filter the list with no way back.
 */
const toDateRange = (
  fromKey: 'createdAtFrom' | 'endDateFrom',
  toKey: 'createdAtTo' | 'endDateTo',
  from?: string | null,
  to?: string | null
) => (isDisbursementBatchesDate(from) && isDisbursementBatchesDate(to) ? { [fromKey]: from, [toKey]: to } : {})

/**
 * Sent as a pair. The table draws a direction caret as soon as a sort column is set, defaulting to
 * descending, so leaving the direction off would let the endpoint apply its own default order under
 * a caret claiming otherwise — and the first header click would then appear to do nothing. A
 * direction without a column to apply it to is dropped for the same reason.
 */
const toSort = (sort?: string | null, direction?: string | null) =>
  isDisbursementBatchesSortField(sort)
    ? {
        sort,
        direction: isDisbursementBatchesSortDirection(direction) ? direction : DisbursementBatchesSortDirection.DESC
      }
    : {}

/**
 * Query params come from the URL and the endpoint is strict, so they are sanitised rather than
 * forwarded: an unrecognised sort, a malformed date or a blank search would 400 the whole list.
 * V2-only params are dropped when the flag is off, so a URL shared by an enabled user cannot
 * silently filter a legacy list that has no control to clear it.
 */
const buildListParams = (
  queryParams: DecodedValueMap<DisbursementsTableQueryParams>,
  isListV2Enabled: boolean
): GetDisbursementBatchesListProps => {
  const shared = {
    allDisbursementsWithStatus: queryParams.allDisbursementsWithStatus,
    anyDisbursementsWithStatus: queryParams.anyDisbursementsWithStatus,
    allDisbursementsWithTypes: queryParams.allDisbursementsWithTypes,
    anyDisbursementsWithTypes: queryParams.anyDisbursementsWithTypes,
    foundationId: queryParams.foundationId
  }

  if (!isListV2Enabled) {
    return shared
  }

  return {
    ...shared,
    search: queryParams.search?.trim() || undefined,
    ...toDateRange('createdAtFrom', 'createdAtTo', queryParams.createdAtFrom, queryParams.createdAtTo),
    ...toDateRange('endDateFrom', 'endDateTo', queryParams.endDateFrom, queryParams.endDateTo),
    ...toSort(queryParams.sort, queryParams.direction)
  }
}

export function DisbursementsContainer() {
  const { disbursementsService } = useServices()

  // The redesigned list drops count columns ops rely on today, so the previous table stays as the
  // flag-off fallback. TODO: delete DisbursementsTableLegacy and this branch once rolled out.
  const isListV2Enabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_DISBURSEMENTS_LIST_V2)

  const [queryParams, setQueryParams] = useQueryParams({
    allDisbursementsWithStatus: ArrayParam,
    anyDisbursementsWithStatus: ArrayParam,
    allDisbursementsWithTypes: ArrayParam,
    anyDisbursementsWithTypes: ArrayParam,
    foundationId: StringParam,
    search: StringParam,
    createdAtFrom: StringParam,
    createdAtTo: StringParam,
    endDateFrom: StringParam,
    endDateTo: StringParam,
    sort: StringParam,
    direction: StringParam
  })

  const [
    { totalResults, isLoading, dataOrNull, errorMessageOrNull },
    { query, nextPage, previousPage, refreshToFirstPage }
  ] = useQueryList(disbursementsService.getDisbursementBatchesList, buildListParams(queryParams, isListV2Enabled))

  useDidMountEffect(() => query(buildListParams(queryParams, isListV2Enabled)), [queryParams, isListV2Enabled, query])

  const tableProps = {
    isLoading,
    totalResults,
    data: dataOrNull,
    previousPage,
    nextPage,
    errorMessage: errorMessageOrNull,
    refreshToFirstPage,
    queryParams,
    setQueryParams
  }

  return isListV2Enabled ? <DisbursementsTable {...tableProps} /> : <DisbursementsTableLegacy {...tableProps} />
}
